import { useState, useEffect } from "react";
import { GlassCard, PageHeader } from "../components/shared";
import { FolderKanban, CheckCircle2, Clock, Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";

export default function Projects() {
    const { user } = useAuth();
    const canManage = ["super_admin", "hr", "lead"].includes(user?.role);
    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [allUsers, setAllUsers] = useState([]);
    const [memberSearch, setMemberSearch] = useState("");
    const [editMemberSearch, setEditMemberSearch] = useState("");

    const [form, setForm] = useState({
        name: "",
        department: "Engineering",
        progress: 0,
        status: "Active",
        leader: "",
        desc: "",
        members: ""
    });

    const [editingProject, setEditingProject] = useState(null);
    const [editForm, setEditForm] = useState({
        name: "",
        department: "Engineering",
        progress: 0,
        status: "Active",
        leader: "",
        desc: "",
        members: ""
    });

    const fetchProjects = async () => {
        try {
            const res = await api.get("/projects");
            setProjects(res.data.items || []);
        } catch (err) {
            toast.error("Failed to load projects");
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const res = await api.get("/chat/users");
            setAllUsers(res.data.users || []);
        } catch (err) {}
    };

    useEffect(() => {
        fetchProjects();
        fetchUsers();
    }, []);

    const addMember = (email, isEdit = false) => {
        const current = isEdit ? editForm.members : form.members;
        const emails = current ? current.split(",").map(m => m.trim()).filter(Boolean) : [];
        if (!emails.includes(email.toLowerCase())) {
            emails.push(email.toLowerCase());
            if (isEdit) {
                setEditForm({ ...editForm, members: emails.join(", ") });
                setEditMemberSearch("");
            } else {
                setForm({ ...form, members: emails.join(", ") });
                setMemberSearch("");
            }
        }
    };

    const removeMember = (email, isEdit = false) => {
        const current = isEdit ? editForm.members : form.members;
        const emails = current ? current.split(",").map(m => m.trim()).filter(Boolean) : [];
        const updated = emails.filter(e => e.toLowerCase() !== email.toLowerCase());
        if (isEdit) {
            setEditForm({ ...editForm, members: updated.join(", ") });
        } else {
            setForm({ ...form, members: updated.join(", ") });
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...form,
                progress: Number(form.progress),
                members: form.members ? form.members.split(",").map(m => m.trim()) : []
            };
            const res = await api.post("/projects", payload);
            toast.success("Project created successfully!");
            
            if (res.data.email_logs && res.data.email_logs.length > 0) {
                res.data.email_logs.forEach(log => {
                    if (log.status === "sent" || log.status === "mocked") {
                        toast.success(`Email notification sent to ${log.email}`);
                    } else {
                        toast.error(`Failed to notify ${log.email}: ${log.message}`);
                    }
                });
            }
            
            setCreateOpen(false);
            setForm({
                name: "",
                department: "Engineering",
                progress: 0,
                status: "Active",
                leader: "",
                desc: "",
                members: ""
            });
            fetchProjects();
        } catch (err) {
            toast.error("Failed to create project");
        }
    };

    const handleEditStart = (project) => {
        setEditingProject(project);
        setEditForm({
            name: project.name,
            department: project.department || project.dept || "Engineering",
            progress: project.progress,
            status: project.status,
            leader: project.leader,
            desc: project.desc,
            members: project.members ? project.members.join(", ") : ""
        });
        setEditOpen(true);
    };

    const handleEditSave = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...editForm,
                progress: Number(editForm.progress),
                members: editForm.members ? editForm.members.split(",").map(m => m.trim()) : []
            };
            const res = await api.put(`/projects/${editingProject.id}`, payload);
            toast.success("Project updated successfully!");
            
            if (res.data.email_logs && res.data.email_logs.length > 0) {
                res.data.email_logs.forEach(log => {
                    if (log.status === "sent" || log.status === "mocked") {
                        toast.success(`Email notification sent to ${log.email}`);
                    } else {
                        toast.error(`Failed to notify ${log.email}: ${log.message}`);
                    }
                });
            }
            
            setEditOpen(false);
            setEditingProject(null);
            fetchProjects();
        } catch (err) {
            toast.error("Failed to update project");
        }
    };

    const handleDelete = async (projectId) => {
        if (!window.confirm("Are you sure you want to delete this project?")) return;
        try {
            await api.delete(`/projects/${projectId}`);
            toast.success("Project deleted successfully!");
            fetchProjects();
        } catch (err) {
            toast.error("Failed to delete project");
        }
    };

    const handleCardClick = (project, e) => {
        if (e.target.closest("button") || e.target.closest("svg")) {
            return;
        }
        setSelectedProject(project);
        setDetailsOpen(true);
    };

    return (
        <div>
            <PageHeader 
                title="Projects" 
                subtitle="Manage and track active organizational goals and team progress." 
                actions={
                    canManage && (
                        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                            <DialogTrigger asChild>
                                <Button className="rounded-full bg-teal-600 hover:bg-teal-700 text-white">
                                    <Plus className="w-4 h-4 mr-1" /> Create project
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-[500px]">
                                <DialogHeader>
                                    <DialogTitle>Create new project</DialogTitle>
                                </DialogHeader>
                                <form onSubmit={handleCreate} className="space-y-4">
                                    <div>
                                        <Label className="text-xs font-semibold text-gray-700">Project Name</Label>
                                        <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label className="text-xs font-semibold text-gray-700">Department</Label>
                                            <Input required value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="mt-1" />
                                        </div>
                                        <div>
                                            <Label className="text-xs font-semibold text-gray-700">Leader (Name or Email)</Label>
                                            <Input required value={form.leader} onChange={(e) => setForm({ ...form, leader: e.target.value })} className="mt-1" />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label className="text-xs font-semibold text-gray-700">Progress (%)</Label>
                                            <Input type="number" min="0" max="100" required value={form.progress} onChange={(e) => setForm({ ...form, progress: e.target.value })} className="mt-1" />
                                        </div>
                                        <div>
                                            <Label className="text-xs font-semibold text-gray-700">Status</Label>
                                            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="mt-1 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm">
                                                <option value="Active">Active</option>
                                                <option value="Completed">Completed</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <Label className="text-xs font-semibold text-gray-700 font-medium">Assign Members (Search by name or type email)</Label>
                                        <div className="relative mt-1">
                                            <Input 
                                                placeholder="Type name to search and select..." 
                                                value={memberSearch} 
                                                onChange={(e) => setMemberSearch(e.target.value)} 
                                                className="text-xs rounded-xl"
                                            />
                                            {memberSearch.trim() && (
                                                <div className="absolute z-30 left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white dark:bg-slate-900 border rounded-xl shadow-lg p-1.5 space-y-1">
                                                    {allUsers
                                                        .filter(u => u.name.toLowerCase().includes(memberSearch.toLowerCase()) || u.email.toLowerCase().includes(memberSearch.toLowerCase()))
                                                        .slice(0, 5)
                                                        .map(u => (
                                                            <div 
                                                                key={u.id}
                                                                onClick={() => addMember(u.email, false)}
                                                                className="flex items-center gap-2.5 p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg cursor-pointer border border-transparent transition text-left"
                                                            >
                                                                {u.avatar ? (
                                                                    <img src={u.avatar} alt={u.name} className="w-6 h-6 rounded-full object-cover" />
                                                                ) : (
                                                                    <div className="w-6 h-6 rounded-full bg-slate-200 text-slate-800 text-[10px] font-bold flex items-center justify-center uppercase">{u.name[0]}</div>
                                                                )}
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="text-xs font-semibold truncate text-slate-700 dark:text-slate-200">{u.name}</div>
                                                                    <div className="text-[10px] text-muted-foreground truncate">{u.email}</div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    {allUsers.filter(u => u.name.toLowerCase().includes(memberSearch.toLowerCase()) || u.email.toLowerCase().includes(memberSearch.toLowerCase())).length === 0 && (
                                                        <div className="text-center text-[10px] text-muted-foreground p-2">No matching users found</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        <Input 
                                            placeholder="e.g. employee1@domain.com, intern2@domain.com" 
                                            value={form.members} 
                                            onChange={(e) => setForm({ ...form, members: e.target.value })} 
                                            className="mt-2 text-xs rounded-xl" 
                                        />
                                        
                                        {form.members.split(",").map(m => m.trim()).filter(Boolean).length > 0 && (
                                            <div className="flex flex-wrap gap-1.5 mt-2">
                                                {form.members.split(",").map(m => m.trim()).filter(Boolean).map((email, idx) => {
                                                    const resolvedUser = allUsers.find(u => u.email.toLowerCase() === email.toLowerCase());
                                                    return (
                                                        <div 
                                                            key={idx}
                                                            className="flex items-center gap-1 pl-2.5 pr-1.5 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-100 text-[10px] font-medium uppercase tracking-wider"
                                                        >
                                                            <span>{resolvedUser ? resolvedUser.name : email.split("@")[0]}</span>
                                                            <button 
                                                                type="button" 
                                                                onClick={() => removeMember(email, false)}
                                                                className="w-3.5 h-3.5 rounded-full hover:bg-teal-200/50 flex items-center justify-center text-teal-800 font-bold"
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <Label className="text-xs font-semibold text-gray-700">Description</Label>
                                        <textarea required value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} className="mt-1 w-full min-h-[80px] rounded-xl border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
                                    </div>
                                    <Button type="submit" className="w-full rounded-xl bg-teal-600 hover:bg-teal-700 text-white h-11 font-semibold">Save</Button>
                                </form>
                            </DialogContent>
                        </Dialog>
                    )
                }
            />

            {/* Edit Dialog */}
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>Edit project</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleEditSave} className="space-y-4">
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Project Name</Label>
                            <Input required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Department</Label>
                                <Input required value={editForm.department} onChange={(e) => setEditForm({ ...editForm, department: e.target.value })} className="mt-1" />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Leader (Name or Email)</Label>
                                <Input required value={editForm.leader} onChange={(e) => setEditForm({ ...editForm, leader: e.target.value })} className="mt-1" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Progress (%)</Label>
                                <Input type="number" min="0" max="100" required value={editForm.progress} onChange={(e) => setEditForm({ ...editForm, progress: e.target.value })} className="mt-1" />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Status</Label>
                                <select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} className="mt-1 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm">
                                    <option value="Active">Active</option>
                                    <option value="Completed">Completed</option>
                                </select>
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700 font-medium">Assign Members (Search by name or type email)</Label>
                            <div className="relative mt-1">
                                <Input 
                                    placeholder="Type name to search and select..." 
                                    value={editMemberSearch} 
                                    onChange={(e) => setEditMemberSearch(e.target.value)} 
                                    className="text-xs rounded-xl"
                                />
                                {editMemberSearch.trim() && (
                                    <div className="absolute z-30 left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white dark:bg-slate-900 border rounded-xl shadow-lg p-1.5 space-y-1">
                                        {allUsers
                                            .filter(u => u.name.toLowerCase().includes(editMemberSearch.toLowerCase()) || u.email.toLowerCase().includes(editMemberSearch.toLowerCase()))
                                            .slice(0, 5)
                                            .map(u => (
                                                <div 
                                                    key={u.id}
                                                    onClick={() => addMember(u.email, true)}
                                                    className="flex items-center gap-2.5 p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg cursor-pointer border border-transparent transition text-left"
                                                >
                                                    {u.avatar ? (
                                                        <img src={u.avatar} alt={u.name} className="w-6 h-6 rounded-full object-cover" />
                                                    ) : (
                                                        <div className="w-6 h-6 rounded-full bg-slate-200 text-slate-800 text-[10px] font-bold flex items-center justify-center uppercase">{u.name[0]}</div>
                                                    )}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-xs font-semibold truncate text-slate-700 dark:text-slate-200">{u.name}</div>
                                                        <div className="text-[10px] text-muted-foreground truncate">{u.email}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        {allUsers.filter(u => u.name.toLowerCase().includes(editMemberSearch.toLowerCase()) || u.email.toLowerCase().includes(editMemberSearch.toLowerCase())).length === 0 && (
                                            <div className="text-center text-[10px] text-muted-foreground p-2">No matching users found</div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <Input 
                                placeholder="e.g. employee1@domain.com, intern2@domain.com" 
                                value={editForm.members} 
                                onChange={(e) => setEditForm({ ...editForm, members: e.target.value })} 
                                className="mt-2 text-xs rounded-xl" 
                            />
                            
                            {editForm.members.split(",").map(m => m.trim()).filter(Boolean).length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                    {editForm.members.split(",").map(m => m.trim()).filter(Boolean).map((email, idx) => {
                                        const resolvedUser = allUsers.find(u => u.email.toLowerCase() === email.toLowerCase());
                                        return (
                                            <div 
                                                key={idx}
                                                className="flex items-center gap-1 pl-2.5 pr-1.5 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-100 text-[10px] font-medium uppercase tracking-wider"
                                            >
                                                <span>{resolvedUser ? resolvedUser.name : email.split("@")[0]}</span>
                                                <button 
                                                    type="button" 
                                                    onClick={() => removeMember(email, true)}
                                                    className="w-3.5 h-3.5 rounded-full hover:bg-teal-200/50 flex items-center justify-center text-teal-800 font-bold"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Description</Label>
                            <textarea required value={editForm.desc} onChange={(e) => setEditForm({ ...editForm, desc: e.target.value })} className="mt-1 w-full min-h-[80px] rounded-xl border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
                        </div>
                        <Button type="submit" className="w-full rounded-xl bg-teal-600 hover:bg-teal-700 text-white h-11 font-semibold">Save changes</Button>
                    </form>
                </DialogContent>
            </Dialog>
            
            {loading ? (
                <div className="flex justify-center items-center py-12">
                    <span className="text-muted-foreground text-sm">Loading projects...</span>
                </div>
            ) : projects.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                    <FolderKanban className="w-12 h-12 text-slate-300 mb-3" />
                    <h3 className="font-heading font-bold text-lg text-slate-800 dark:text-slate-100">No projects found</h3>
                    <p className="text-xs text-muted-foreground max-w-xs mt-1">
                        {canManage ? "Get started by creating a new project tracking card." : "You are not assigned to any projects yet."}
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {projects.map((p) => (
                        <GlassCard 
                            key={p.id} 
                            onClick={(e) => handleCardClick(p, e)}
                            className="p-6 relative overflow-hidden flex flex-col justify-between h-64 border border-teal-100/50 dark:border-teal-900/50 hover:shadow-md transition duration-300 cursor-pointer"
                        >
                            <div>
                                <div className="flex justify-between items-start mb-4">
                                    <div className="p-2.5 rounded-xl bg-teal-50 text-teal-600 dark:bg-teal-950/20 dark:text-teal-400">
                                        <FolderKanban className="w-5 h-5" />
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full flex items-center gap-1
                                            ${p.status === "Completed" 
                                                ? "bg-emerald-50 text-emerald-600 border border-emerald-200 dark:bg-emerald-950/20 dark:text-emerald-400"
                                                : "bg-blue-50 text-blue-600 border border-blue-200 dark:bg-blue-950/20 dark:text-blue-400"
                                            }`}>
                                            {p.status === "Completed" ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                                            {p.status}
                                        </span>
                                        {canManage && (
                                            <div className="flex gap-1">
                                                <button onClick={() => handleEditStart(p)} className="p-1 text-slate-400 hover:text-teal-600 transition" title="Edit project">
                                                    <Pencil className="w-3.5 h-3.5" />
                                                </button>
                                                <button onClick={() => handleDelete(p.id)} className="p-1 text-slate-400 hover:text-red-600 transition" title="Delete project">
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <h3 className="font-heading font-bold text-lg text-slate-800 dark:text-slate-100">{p.name}</h3>
                                <span className="text-xs font-semibold text-muted-foreground">{p.department || p.dept} · Lead: {p.leader}</span>
                                <p className="text-xs text-slate-500 mt-2 line-clamp-2">{p.desc}</p>
                            </div>
                            
                            <div className="mt-4">
                                <div className="flex justify-between items-center text-xs font-semibold mb-1">
                                    <span>Progress</span>
                                    <span>{p.progress}%</span>
                                </div>
                                <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-1.5 overflow-hidden">
                                    <div 
                                        className="bg-teal-600 h-1.5 rounded-full transition-all duration-500" 
                                        style={{ width: `${p.progress}%` }}
                                    />
                                </div>
                            </div>
                        </GlassCard>
                    ))}
                </div>
            )}

            {/* Project Details Dialog */}
            <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle className="text-lg font-bold font-heading text-slate-800 dark:text-slate-100">Project Details</DialogTitle>
                    </DialogHeader>
                    {selectedProject && (
                        <div className="space-y-4 py-2">
                            <div>
                                <h3 className="text-base font-bold text-teal-700 dark:text-teal-400 font-heading">{selectedProject.name}</h3>
                                <p className="text-xs text-muted-foreground font-semibold">{selectedProject.department} · Lead: {selectedProject.leader}</p>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4 border-y border-black/5 dark:border-white/5 py-3">
                                <div>
                                    <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider block">Status</span>
                                    <span className={`inline-block text-xs font-semibold px-2 py-0.5 mt-1 rounded-full border
                                        ${selectedProject.status === "Completed"
                                            ? "bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-950/20 dark:text-emerald-400"
                                            : "bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-950/20 dark:text-blue-400"
                                        }`}>
                                        {selectedProject.status}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider block">Progress</span>
                                    <div className="flex items-center gap-2 mt-1">
                                        <div className="flex-1 bg-slate-100 dark:bg-slate-800 rounded-full h-2 overflow-hidden">
                                            <div className="bg-teal-600 h-2 rounded-full" style={{ width: `${selectedProject.progress}%` }} />
                                        </div>
                                        <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">{selectedProject.progress}%</span>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider block mb-1">Assigned Members</span>
                                {selectedProject.members && selectedProject.members.length > 0 ? (
                                    <div className="flex flex-wrap gap-2 mt-1">
                                        {selectedProject.members.map((email, idx) => {
                                            const resolved = allUsers.find(u => u.email.toLowerCase() === email.toLowerCase());
                                            return (
                                                <div 
                                                    key={idx}
                                                    className="flex items-center gap-1.5 pl-2 py-1 pr-3 rounded-full bg-slate-50 dark:bg-slate-900 border text-xs"
                                                >
                                                    {resolved?.avatar ? (
                                                        <img src={resolved.avatar} alt={resolved.name} className="w-5 h-5 rounded-full object-cover" />
                                                    ) : (
                                                        <div className="w-5 h-5 rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold flex items-center justify-center uppercase">
                                                            {resolved ? resolved.name[0] : email[0]}
                                                        </div>
                                                    )}
                                                    <div className="text-left leading-none">
                                                        <div className="text-[11px] font-semibold text-slate-700 dark:text-slate-200">{resolved ? resolved.name : email.split("@")[0]}</div>
                                                        <div className="text-[9px] text-muted-foreground font-mono">{email}</div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <p className="text-xs text-muted-foreground italic">No members assigned</p>
                                )}
                            </div>

                            <div>
                                <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider block mb-1">Work Description</span>
                                <div className="bg-slate-50/50 dark:bg-slate-900/50 border rounded-xl p-3 text-xs text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
                                    {selectedProject.desc}
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
